import React, { useCallback, useEffect, useState } from "react";
import { DebtBreadcrumb, DebtContainer, DebtTitle, BreadcrumbItem, BreadcrumbSeparator } from "../DebtManagement/styled/DebtManagementStyles";
import { MyBookingWrapper } from "../MyBooking/styled/DesStyled";
import { OrderInner } from "../Order/styled/OrderStyle";
import {
	BatchGroup,
	BatchRow,
	apiErrorText,
	createBatch,
	getBatchConfig,
	getEligibleGroups,
	listBatches,
	refreshBatch,
	setBatchConfig,
} from "../../../services/PayoutBatchService";

/**
 * BATCH PAYOUTS — gộp lệnh Wise theo BÊN KÝ (B3 chuỗi, Quân chốt 17/10/2026).
 * Một lô = MỘT lệnh Wise trả nhiều yêu cầu đã duyệt của cùng bên ký, cùng tiền, cùng người nhận,
 * đều Above Min (WhaleLO chịu phí). Maker–checker như Pay qua Wise từng lệnh: máy tạo lệnh,
 * Quân duyệt trong app Wise, máy tự gạt PAID từng yêu cầu khi tiền đi. Giao diện TIẾNG ANH.
 */

const cell: React.CSSProperties = { padding: "8px 10px", borderBottom: "1px solid #eaecf0", verticalAlign: "top" };
const mono: React.CSSProperties = { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 13 };
const btn = (primary = false): React.CSSProperties => ({
	padding: "6px 12px",
	borderRadius: 6,
	cursor: "pointer",
	border: primary ? 0 : "1px solid #d0d5dd",
	background: primary ? "#1570ef" : "#fff",
	color: primary ? "#fff" : "#344054",
});
const fmt = (n: number | null | undefined) => (n == null ? "—" : Number(n).toLocaleString("en-US"));
const STATUS: Record<string, string> = {
	AWAITING_APPROVE: "Waiting for approval in Wise",
	SENT: "Sent — on the way",
	PAID: "Paid",
	CANCELLED: "Cancelled in Wise",
	FAILED: "Failed",
};

const PayoutBatches: React.FC = () => {
	const [enabled, setEnabled] = useState<boolean | null>(null);
	const [groups, setGroups] = useState<BatchGroup[]>([]);
	const [batches, setBatches] = useState<BatchRow[]>([]);
	const [loading, setLoading] = useState(false);
	const [busy, setBusy] = useState<string | null>(null);
	const [notice, setNotice] = useState<{ tone: "ok" | "err"; text: string } | null>(null);

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const [cfg, el, bs] = await Promise.all([getBatchConfig(), getEligibleGroups(), listBatches()]);
			setEnabled(cfg.enabled);
			setGroups(el.groups);
			setBatches(bs);
		} catch (e) {
			setNotice({ tone: "err", text: apiErrorText(e) });
		} finally {
			setLoading(false);
		}
	}, []);
	useEffect(() => {
		load();
	}, [load]);

	const toggle = async () => {
		if (enabled === null) return;
		const next = !enabled;
		if (!window.confirm(next ? "Turn ON batch payouts? Nothing is paid until someone presses Pay together and Quân approves in Wise." : "Turn OFF batch payouts?")) return;
		setBusy("cfg");
		try {
			const r = await setBatchConfig(next);
			setEnabled(r.enabled);
			setNotice({ tone: "ok", text: `Batch payouts ${r.enabled ? "turned on" : "turned off"}.` });
		} catch (e) {
			setNotice({ tone: "err", text: apiErrorText(e) });
		} finally {
			setBusy(null);
		}
	};

	const pay = async (g: BatchGroup) => {
		const key = g.requestIds.join(",");
		if (
			!window.confirm(
				`Create ONE Wise transfer of ${fmt(g.targetTotal)} ${g.currency} for ${g.requestIds.length} requests of ${g.legalName || "legal entity #" + g.legalEntityId}? Quân still approves it in the Wise app.`,
			)
		)
			return;
		setBusy(key);
		try {
			const r = await createBatch(g.requestIds);
			setNotice({ tone: "ok", text: `Batch B${r.batchId} created: ${r.itemCount} requests, ${fmt(r.targetTotal)} ${r.currency} (${r.sourceTotal} USD). Approve "${r.reference}" in Wise.` });
			await load();
		} catch (e) {
			setNotice({ tone: "err", text: apiErrorText(e) });
		} finally {
			setBusy(null);
		}
	};

	const refresh = async (b: BatchRow) => {
		setBusy(`b${b.id}`);
		try {
			await refreshBatch(b.id);
			await load();
			setNotice({ tone: "ok", text: `Batch B${b.id} refreshed from Wise.` });
		} catch (e) {
			setNotice({ tone: "err", text: apiErrorText(e) });
		} finally {
			setBusy(null);
		}
	};

	return (
		<MyBookingWrapper>
			<OrderInner>
				<DebtContainer>
					<DebtBreadcrumb>
						<BreadcrumbItem as="a" href="/" style={{ textDecoration: "none" }}>
							{"Home"}
						</BreadcrumbItem>
						<BreadcrumbSeparator src="./static/img/direction-right.png" alt=">" />
						<BreadcrumbItem active>{"Batch payouts"}</BreadcrumbItem>
					</DebtBreadcrumb>
					<DebtTitle>Batch payouts</DebtTitle>

					<p style={{ color: "#555", maxWidth: 900, lineHeight: 1.6 }}>
						Pay several approved requests of the <b>same legal entity</b> with <b>one Wise transfer</b> — same currency, same Wise recipient, all
						<b> Above Min</b> (WhaleLO pays the fee). Each station's balance and its 200 USD minimum are unchanged; only the number of transfers goes down.
						Quân still approves every transfer in the Wise app; each request is marked paid with its own amount when the money leaves.
					</p>

					<div style={{ display: "flex", gap: 12, alignItems: "center", margin: "8px 0 16px" }}>
						<span>
							Batch payouts:{" "}
							<b style={{ color: enabled ? "#027a48" : "#b42318" }}>{enabled === null ? "…" : enabled ? "ON" : "OFF"}</b>
						</span>
						<button type="button" style={btn()} disabled={busy === "cfg" || enabled === null} onClick={toggle}>
							{enabled ? "Turn off" : "Turn on"}
						</button>
						<button type="button" style={btn()} disabled={loading} onClick={load}>
							{loading ? "Loading…" : "Refresh"}
						</button>
					</div>

					{notice ? <p style={{ color: notice.tone === "ok" ? "#027a48" : "#b42318", fontWeight: 600 }}>{notice.text}</p> : null}

					<h3 style={{ margin: "16px 0 8px" }}>Can be paid together now</h3>
					{!loading && groups.length === 0 ? <p style={{ color: "#555" }}>No group of two or more requests qualifies right now.</p> : null}
					<div style={{ overflowX: "auto" }}>
						{groups.length ? (
							<table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
								<thead>
									<tr style={{ background: "#f9fafb", textAlign: "left" }}>
										<th style={cell}>Legal entity</th>
										<th style={cell}>Requests</th>
										<th style={cell}>Total</th>
										<th style={cell}>Wise recipient</th>
										<th style={cell}></th>
									</tr>
								</thead>
								<tbody>
									{groups.map((g) => (
										<tr key={g.requestIds.join(",")}>
											<td style={cell}>
												<b>{g.legalName || `#${g.legalEntityId}`}</b>
											</td>
											<td style={cell}>
												{g.items.map((x) => (
													<div key={x.requestId} style={{ fontSize: 13 }}>
														#{x.requestId} · {x.partnerName} · {fmt(x.amount)} {x.currency}
														{x.nguonNguoiNhan === "cung-ben-ky" ? <span style={{ color: "#667085" }}> · shared recipient</span> : null}
													</div>
												))}
											</td>
											<td style={{ ...cell, fontVariantNumeric: "tabular-nums" }}>
												<b>
													{fmt(g.targetTotal)} {g.currency}
												</b>
											</td>
											<td style={{ ...cell, ...mono }}>{g.wiseRecipientId}</td>
											<td style={cell}>
												<button type="button" style={btn(true)} disabled={!enabled || busy === g.requestIds.join(",")} onClick={() => pay(g)} title={enabled ? "" : "Turn on batch payouts first"}>
													{busy === g.requestIds.join(",") ? "Creating…" : "Pay together"}
												</button>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						) : null}
					</div>

					<h3 style={{ margin: "24px 0 8px" }}>Recent batches</h3>
					{batches.length === 0 ? <p style={{ color: "#555" }}>No batch yet.</p> : null}
					<div style={{ overflowX: "auto" }}>
						{batches.length ? (
							<table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
								<thead>
									<tr style={{ background: "#f9fafb", textAlign: "left" }}>
										<th style={cell}>Batch</th>
										<th style={cell}>Legal entity</th>
										<th style={cell}>Requests</th>
										<th style={cell}>Total</th>
										<th style={cell}>Status</th>
										<th style={cell}>Reference</th>
										<th style={cell}></th>
									</tr>
								</thead>
								<tbody>
									{batches.map((b) => (
										<tr key={b.id}>
											<td style={{ ...cell, ...mono }}>B{b.id}</td>
											<td style={cell}>#{b.legalEntityId}</td>
											<td style={cell}>{b.itemCount}</td>
											<td style={{ ...cell, fontVariantNumeric: "tabular-nums" }}>
												{fmt(b.targetTotal)} {b.targetCurrency}
												{b.sourceTotal ? <span style={{ color: "#667085" }}> · {b.sourceTotal} USD</span> : null}
											</td>
											<td style={cell}>{STATUS[b.status] || b.status}</td>
											<td style={{ ...cell, ...mono }}>{b.reference || "—"}</td>
											<td style={cell}>
												{b.status === "AWAITING_APPROVE" || b.status === "SENT" ? (
													<button type="button" style={btn()} disabled={busy === `b${b.id}`} onClick={() => refresh(b)}>
														Check Wise
													</button>
												) : null}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						) : null}
					</div>
				</DebtContainer>
			</OrderInner>
		</MyBookingWrapper>
	);
};

export default PayoutBatches;
